/* eslint-disable import/prefer-default-export */
import Postmate from 'postmate';
import { isNil } from '@flowio/is';
import { Callbacks } from '@flowio/callbacks';

import { removeElement, resolveElement } from '../../utilities/elementHelpers';
import getPagesLocation from '../../utilities/getPagesLocation';

import type {
  CardManagedPaymentMethodParameter, DomReference, RequestPaymentParameters,
} from '../../types';
import type {
  CardCallbackParameters,
  CardCompleteData,
  CardCompleteError,
  CardCompleteSuccess,
  CardFormInitOptions,
  CardTokenizeOptions,
} from './types';
import convertPaymentAddrToBillingAddr from '../../utilities/convertPaymentAddrToBillingAddr';
import getDeviceFingerprintDetails from '../../utilities/getDeviceFingerprintDetails';

const TOKENIZATION_TIMEOUT = 10 * 1000;

type CardManagedContainers = {
  button: HTMLDivElement;
  form: HTMLDivElement;
};

const cardIframeClassname = 'flow-payment-sdk-card-iframe';

function bootstrapElements(topContainer: DomReference): CardManagedContainers {
  const topContainerEl = resolveElement(topContainer);

  const formContainer = document.createElement('div');
  topContainerEl.appendChild(formContainer);
  formContainer.classList.add('flow-sdk-container');
  const buttonContainer = document.createElement('div');
  topContainerEl.appendChild(buttonContainer);
  buttonContainer.classList.add('flow-sdk-container');
  return {
    button: buttonContainer,
    form: formContainer,
  };
}

function getCardFormUrl(): string {
  const pagesLoc = getPagesLocation();

  return `${pagesLoc}/pages/card_form/index.html`;
}

function createCardSubmitButton(
  pm: CardManagedPaymentMethodParameter,
  container: HTMLElement,
): HTMLButtonElement | HTMLElement {
  if (pm.render.button) {
    const button = resolveElement(pm.render.button);
    return button;
  }
  const buttonText = pm.render.buttonText || 'Pay with Card';
  const button = document.createElement('button');
  button.appendChild(document.createTextNode(buttonText));
  button.classList.add('flow-sdk-button');
  container.appendChild(button);
  return button;
}

function isCardCompleteSuccess(cardData: CardCompleteData): cardData is CardCompleteSuccess {
  return isNil((cardData as CardCompleteError).error);
}

export default function cardManagedForm(
  params: RequestPaymentParameters,
): Promise<io.flow.v0.models.PaymentMethodDataAuthorizeCard> {
  return new Promise((resolve, reject) => {
    const pm = params.data.paymentMethod;
    if (!isNil(pm) && pm.id === 'card' && pm.type === 'managed_form') {
      const callbacks = new Callbacks();
      const iframeClassname = cardIframeClassname;

      const iFrameStyleEl = document.createElement('style');
      document.head.appendChild(iFrameStyleEl);
      iFrameStyleEl.sheet?.insertRule(`
        .${iframeClassname} {
          width: 100%;
          border: 0;
        }
      `);

      const containers = bootstrapElements(pm.render.mountContainer);

      const handshake = new Postmate({
        url: getCardFormUrl(),
        container: containers.form,
        classListArray: [iframeClassname],
      });

      const cardButton = createCardSubmitButton(pm, containers.button);

      handshake.then((child) => {
        // Fetch the height property in child.html and set it to the iFrames height
        child
          .get('height')
          .then((height) => {
            // eslint-disable-next-line no-param-reassign
            child.frame.style.height = `${height}px`;
          });

        const initOpts: CardFormInitOptions = {
          organizationId: params.config.organization,
          genericMobileImprovements: true,
          errorMessages: pm.render.errorMessages,
          messages: pm.render.messages,
          styles: pm.render.styles,
        };
        child.call('init', initOpts);

        child.on('callback', (data: CardCallbackParameters) => {
          if (data.callbackId) {
            const { callbackId, ...rest } = data;
            callbacks.invoke(data.callbackId, rest);
            callbacks.remove(data.callbackId);
          }
        });

        child.on('resize', ({ height }) => {
          // eslint-disable-next-line no-param-reassign
          child.frame.style.height = `${height}px`;
        });

        cardButton.addEventListener('click', () => {
          const timeoutId = window.setTimeout(() => {
            reject(new Error('Timeout while tokenizing card'));
          }, TOKENIZATION_TIMEOUT);
          const tokenizeOpts: CardTokenizeOptions = {
            callbackId: callbacks.add((data: CardCompleteData) => {
              window.clearTimeout(timeoutId);
              if (isCardCompleteSuccess(data)) {
                if (data.status >= 200 && data.status < 300) {
                  resolve({
                    type: 'authorize_card',
                    card_data: {
                      type: 'token',
                      token: data.response.token,
                    },
                    device_fingerprint_details: getDeviceFingerprintDetails(),
                  });
                  child.destroy();
                  removeElement(containers.button);
                  removeElement(containers.form);
                }
              }
            }),
            requestedCurrency: params.data.currency,
          };

          // eslint-disable-next-line max-len
          tokenizeOpts.billingAddress = convertPaymentAddrToBillingAddr(
            params.data.orderInformation.billingAddress,
          );
          child.call('tokenize', tokenizeOpts);
        });
      });
    } else {
      reject(new Error('createCardIframe: called with incorrect requestPayment parameters'));
    }
  });
}
